import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, BookOpen, Pencil, X } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { platform: "", title: "", content: "" };

export function ModGuidelines() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/mod/guidelines").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEdit = (g) => {
    setEditId(g.id);
    setForm({ platform: g.platform, title: g.title, content: g.content || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const reset = () => { setEditId(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.platform.trim() || !form.title.trim()) {
      toast.error("Platform és cím kötelező");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/mod/guidelines/${editId}`, form);
        toast.success("Irányelv frissítve");
      } else {
        await api.post("/mod/guidelines", form);
        toast.success("Irányelv létrehozva");
      }
      reset();
      await load();
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/mod/guidelines/${id}`);
    if (editId === id) reset();
    await load();
    toast.success("Irányelv törölve");
  };

  const byPlatform = items.reduce((acc, g) => {
    (acc[g.platform] = acc[g.platform] || []).push(g);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-4xl" data-testid="mod-guidelines">
      <div className="mb-4">
        <h2 className="text-lg font-semibold tracking-tight text-zinc-50">Közösségi irányelvek</h2>
        <p className="text-[12px] text-zinc-500">
          Platformonként hozz létre irányelveket. Az agentek a bejelentés nézetéből megnyithatják ezeket, és a döntéskor hivatkozhatnak rájuk.
        </p>
      </div>

      {/* editor */}
      <div className="rounded-xl border border-zinc-800/70 bg-[#111113] p-4">
        <div className="mb-3 flex items-center gap-2 text-[13px] font-medium text-zinc-200">
          {editId ? <Pencil size={15} className="text-blue-400" /> : <Plus size={15} className="text-blue-400" />}
          {editId ? "Irányelv szerkesztése" : "Új irányelv"}
          {editId && (
            <button data-testid="guideline-cancel-edit" onClick={reset} className="ml-auto inline-flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-300">
              <X size={13} /> Mégse
            </button>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">Platform *</div>
            <Input data-testid="guideline-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} placeholder="pl. Roblox, TikTok" className="h-9 border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100" />
          </div>
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">Cím *</div>
            <Input data-testid="guideline-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="pl. Gyűlöletbeszéd szabályzat" className="h-9 border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100" />
          </div>
        </div>
        <div className="mt-3">
          <div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">Tartalom</div>
          <Textarea data-testid="guideline-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Az irányelv szövege…" className="min-h-[120px] border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100" />
        </div>
        <div className="mt-3 flex justify-end">
          <Button data-testid="guideline-save" onClick={save} disabled={saving} className="h-9 gap-2 bg-blue-600 px-5 text-[13px] font-semibold text-white hover:bg-blue-500">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {editId ? "Mentés" : "Létrehozás"}
          </Button>
        </div>
      </div>

      {/* list */}
      <div className="mt-5 space-y-5">
        {items.length === 0 && <div className="rounded-lg border border-zinc-800/70 bg-[#111113] p-6 text-center text-[13px] text-zinc-600">Még nincs irányelv.</div>}
        {Object.keys(byPlatform).sort().map((platform) => (
          <div key={platform}>
            <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-zinc-400">
              <BookOpen size={13} className="text-blue-400" /> {platform}
              <span className="text-zinc-600">({byPlatform[platform].length})</span>
            </div>
            <div className="space-y-2">
              {byPlatform[platform].map((g) => (
                <div key={g.id} data-testid={`guideline-item-${g.id}`} className="rounded-lg border border-zinc-800/70 bg-[#111113] p-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[14px] font-medium text-zinc-100">{g.title}</div>
                      {g.content && <p className="mt-1 whitespace-pre-wrap text-[12px] leading-relaxed text-zinc-400">{g.content}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button data-testid={`guideline-edit-${g.id}`} onClick={() => startEdit(g)} className="grid h-8 w-8 place-items-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><Pencil size={14} /></button>
                      <button data-testid={`guideline-delete-${g.id}`} onClick={() => remove(g.id)} className="grid h-8 w-8 place-items-center rounded-md text-red-400/70 hover:bg-red-950/40 hover:text-red-300"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
